import { useState, useEffect } from "react";
import useSWR from "swr";
import { api, fmtEur, fmtNum, fmtPct, WEEKDAYS_SK } from "@/lib/app";
import { Plus, Trash, Flask, ArrowUp, ArrowDown } from "@phosphor-icons/react";
import { toast } from "sonner";
import Scenarios from "@/components/Scenarios";

const fetcher = (url) => api.get(url).then((r) => r.data);

function DeltaLine({ label, base, modified, fmt = fmtNum, suffix = "" }) {
  const delta = modified - base;
  const pct = base ? (delta / base) * 100 : 0;
  const tone = delta > 0 ? "text-[var(--critical)]" : delta < 0 ? "text-[var(--success)]" : "text-[var(--text-muted)]";
  return (
    <div className="flex items-center justify-between text-sm py-1 border-b border-[var(--border-default)] last:border-0">
      <span className="text-[var(--text-secondary)]">{label}</span>
      <span className="tabular flex items-center gap-3">
        <span className="text-[var(--text-secondary)]">{fmt(base)}{suffix}</span>
        <span>→</span>
        <span className="font-semibold">{fmt(modified)}{suffix}</span>
        <span className={`text-xs ${tone} min-w-[70px] text-right`}>
          {delta > 0 ? "+" : ""}{fmt(delta)}{suffix} {base ? `(${pct > 0 ? "+" : ""}${pct.toFixed(1)} %)` : ""}
        </span>
      </span>
    </div>
  );
}

export default function WhatIf({ storeId, range, period }) {
  const [forecastFrom, setForecastFrom] = useState("");
  const [forecastDays, setForecastDays] = useState(14);
  const [forecastTo, setForecastTo] = useState("");
  const [hourlyWage, setHourlyWage] = useState(8);
  const [mods, setMods] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  // open hours for the store
  const { data: store } = useSWR(`/stores/${storeId}`, fetcher);

  useEffect(() => {
    if (!forecastFrom) {
      // Default to TODAY (forward-looking simulation), not day-after-range.max
      const t = new Date();
      const end = new Date(t);
      end.setDate(end.getDate() + forecastDays - 1);
      setForecastFrom(t.toISOString().slice(0, 10));
      setForecastTo(end.toISOString().slice(0, 10));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range?.max]);

  useEffect(() => {
    if (!forecastFrom) return;
    const s = new Date(forecastFrom);
    const e = new Date(s);
    e.setDate(e.getDate() + forecastDays - 1);
    setForecastTo(e.toISOString().slice(0, 10));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forecastDays, forecastFrom]);

  const addMod = () => {
    setMods((m) => [...m, { weekday: 4, hour_from: 16, hour_to: 18, delta_employees: 0 }]);
  };
  const updateMod = (i, key, val) => {
    setMods((m) => m.map((x, idx) => (idx === i ? { ...x, [key]: val } : x)));
  };
  const removeMod = (i) => setMods((m) => m.filter((_, idx) => idx !== i));

  // Helper: estimate cost impact of one modification given current forecast horizon
  const estimateModCost = (m) => {
    const hours = Math.max(0, (m.hour_to || 0) - (m.hour_from || 0));
    // approx count of weekday occurrences in forecast horizon
    const days = Math.max(0, forecastDays);
    const occ = Math.max(1, Math.round(days / 7));
    const dph = (m.delta_employees || 0) * hours * occ;
    return { personhours: dph, cost: dph * hourlyWage, occurrences: occ, hours };
  };

  const simulate = async () => {
    setLoading(true);
    try {
      const res = await api.post("/whatif", {
        store_id: storeId,
        period_from: period.from,
        period_to: period.to,
        forecast_from: forecastFrom,
        forecast_to: forecastTo,
        hourly_wage: hourlyWage,
        modifications: mods,
      });
      setResult(res.data);
      toast.success("Simulácia vypočítaná");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Chyba pri simulácii");
    } finally {
      setLoading(false);
    }
  };

  const sum = result?.summary;

  const loadScenario = (s) => {
    setForecastFrom(s.forecast_from);
    setForecastTo(s.forecast_to);
    // derive days from interval
    try {
      const a = new Date(s.forecast_from);
      const b = new Date(s.forecast_to);
      const diff = Math.max(1, Math.round((b - a) / 86400000) + 1);
      setForecastDays(diff);
    } catch { /* ignore */ }
    setHourlyWage(s.hourly_wage || 8);
    setMods((s.modifications || []).map((m) => ({
      weekday: m.weekday,
      hour_from: m.hour_from,
      hour_to: m.hour_to,
      delta_employees: m.delta_employees,
    })));
    setResult(null);
    toast.success(`Načítaný scenár: ${s.name}`);
  };

  return (
    <div className="space-y-6">
      <div className="card-flat p-4 bg-[var(--brand)]/5 border-[var(--brand)]/30">
        <h2 className="font-display text-lg font-semibold inline-flex items-center gap-2">
          <Flask size={20} weight="duotone" className="text-[var(--brand)]" /> Simulácia do budúcna + reflexia minulosti
        </h2>
        <p className="text-xs text-[var(--text-secondary)] mt-1">
          <strong>Hore</strong> — čo by sa stalo, keby ste od dnes upravili rozvrh (forecast + úpravy + dopad na náklady).
          <strong> Dole</strong> — čo sa stalo v minulosti a kde bola možnosť ušetriť alebo zarobiť viac.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-5">
      {/* Left panel: inputs */}
      <div className="space-y-4">
        <div className="card-flat p-5">
          <h3 className="font-display text-base font-semibold mb-1 inline-flex items-center gap-2">
            <Flask size={18} weight="duotone" className="text-[var(--brand)]" /> Parametre simulácie
          </h3>
          <p className="text-xs text-[var(--text-secondary)] mb-4">
            Forecast personálu vychádza z trénovacieho obdobia hore. Pridajte zmeny v rozvrhu a uvidíte dopad na náklady a efektívnosť.
          </p>

          <div className="space-y-3">
            <div>
              <label className="label-eyebrow block mb-1">Trénovacie obdobie</label>
              <p className="text-sm tabular">{period.from} → {period.to}</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-eyebrow block mb-1">Forecast od</label>
                <input type="date" value={forecastFrom} onChange={(e) => setForecastFrom(e.target.value)} className="w-full" data-testid="wi-forecast-from" />
              </div>
              <div>
                <label className="label-eyebrow block mb-1">Dní</label>
                <input
                  type="number"
                  min="1"
                  max="90"
                  value={forecastDays}
                  onChange={(e) => setForecastDays(Math.max(1, Math.min(90, parseInt(e.target.value) || 1)))}
                  className="w-full tabular"
                  data-testid="wi-forecast-days"
                />
              </div>
            </div>
            <div>
              <label className="label-eyebrow block mb-1">Rýchle voľby</label>
              <div className="flex flex-wrap gap-1.5">
                {[
                  { id: "today-7", label: "7 dní", days: 7, fromToday: true },
                  { id: "today-14", label: "14 dní", days: 14, fromToday: true },
                  { id: "today-30", label: "30 dní", days: 30, fromToday: true },
                  { id: "eom", label: "Do konca mesiaca", days: null, fromToday: true, eom: true },
                ].map((p) => (
                  <button
                    key={p.id}
                    onClick={() => {
                      const today = new Date();
                      const from = today.toISOString().slice(0, 10);
                      let to;
                      if (p.eom) {
                        const last = new Date(today.getFullYear(), today.getMonth() + 1, 0);
                        to = last.toISOString().slice(0, 10);
                      } else {
                        const end = new Date(today);
                        end.setDate(end.getDate() + p.days - 1);
                        to = end.toISOString().slice(0, 10);
                      }
                      const diff = Math.max(1, Math.round((new Date(to) - new Date(from)) / 86400000) + 1);
                      setForecastFrom(from);
                      setForecastTo(to);
                      setForecastDays(diff);
                    }}
                    className="text-xs px-2.5 py-1 rounded-full border border-[var(--border-default)] bg-white text-[var(--text-secondary)] hover:border-[var(--brand)] hover:text-[var(--text-primary)]"
                    data-testid={`wi-preset-${p.id}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-[var(--text-muted)] mt-1.5">
                Tlačidlá nastavia obdobie <strong>od dnes</strong> dopredu.
              </p>
            </div>
            <div>
              <label className="label-eyebrow block mb-1">Hodinová mzda (€)</label>
              <input
                type="number"
                step="0.5"
                value={hourlyWage}
                onChange={(e) => setHourlyWage(parseFloat(e.target.value) || 0)}
                className="w-32 tabular"
                data-testid="wi-hourly-wage"
              />
            </div>
          </div>
        </div>

        <div className="card-flat p-5">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-display text-sm font-semibold">Zmeny v rozvrhu</h4>
            <button onClick={addMod} className="btn-ghost text-xs inline-flex items-center gap-1" data-testid="wi-add-mod">
              <Plus size={12} /> Pridať
            </button>
          </div>

          {mods.length === 0 && (
            <p className="text-xs text-[var(--text-muted)] italic">Žiadne úpravy — bude sa simulovať čistý forecast.</p>
          )}

          <div className="space-y-3">
            {mods.map((m, i) => {
              const est = estimateModCost(m);
              return (
              <div key={i} className="bg-[var(--bg-page)] rounded p-3 space-y-2" data-testid={`wi-mod-${i}`}>
                <div className="flex items-center justify-between">
                  <span className="label-eyebrow">Úprava #{i + 1}</span>
                  <button onClick={() => removeMod(i)} className="text-[var(--text-muted)] hover:text-[var(--critical)]" data-testid={`wi-mod-${i}-remove`}>
                    <Trash size={12} />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <select
                    value={m.weekday}
                    onChange={(e) => updateMod(i, "weekday", parseInt(e.target.value))}
                    className="text-xs"
                    data-testid={`wi-mod-${i}-weekday`}
                  >
                    {WEEKDAYS_SK.map((w, idx) => (
                      <option key={idx} value={idx}>{w}</option>
                    ))}
                  </select>
                  <div className="flex items-center gap-1 text-xs">
                    <span className="text-[var(--text-muted)]">Δ os:</span>
                    <button
                      onClick={() => updateMod(i, "delta_employees", (m.delta_employees || 0) - 1)}
                      className="btn-ghost w-7 h-7 flex items-center justify-center p-0"
                      data-testid={`wi-mod-${i}-minus`}
                    >
                      <ArrowDown size={12} />
                    </button>
                    <span className={`tabular font-semibold w-8 text-center ${m.delta_employees > 0 ? "text-[var(--critical)]" : m.delta_employees < 0 ? "text-[var(--success)]" : ""}`}>
                      {m.delta_employees > 0 ? "+" : ""}{m.delta_employees}
                    </span>
                    <button
                      onClick={() => updateMod(i, "delta_employees", (m.delta_employees || 0) + 1)}
                      className="btn-ghost w-7 h-7 flex items-center justify-center p-0"
                      data-testid={`wi-mod-${i}-plus`}
                    >
                      <ArrowUp size={12} />
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[0.65rem] uppercase tracking-wider text-[var(--text-muted)]">Od hodiny</label>
                    <input
                      type="number"
                      min="0"
                      max="23"
                      value={m.hour_from}
                      onChange={(e) => updateMod(i, "hour_from", parseInt(e.target.value) || 0)}
                      className="w-full tabular text-sm"
                      data-testid={`wi-mod-${i}-from`}
                    />
                  </div>
                  <div>
                    <label className="text-[0.65rem] uppercase tracking-wider text-[var(--text-muted)]">Do hodiny</label>
                    <input
                      type="number"
                      min="1"
                      max="24"
                      value={m.hour_to}
                      onChange={(e) => updateMod(i, "hour_to", parseInt(e.target.value) || 1)}
                      className="w-full tabular text-sm"
                      data-testid={`wi-mod-${i}-to`}
                    />
                  </div>
                </div>
                {/* Live cost-impact preview for this single modification */}
                <div className="border-t border-[var(--border-default)] pt-2 mt-1 flex items-center justify-between text-[0.7rem]">
                  <span className="text-[var(--text-muted)]">
                    {est.occurrences}× {WEEKDAYS_SK[m.weekday]} × {est.hours} h ={" "}
                    <span className="tabular font-semibold text-[var(--text-primary)]">
                      {est.personhours > 0 ? "+" : ""}{fmtNum(est.personhours, 0)} os.hod
                    </span>
                  </span>
                  <span
                    className={`tabular font-semibold ${est.cost > 0 ? "text-[var(--critical)]" : est.cost < 0 ? "text-[var(--success)]" : "text-[var(--text-muted)]"}`}
                    title="Odhad nákladového dopadu pri zadanej hodinovej mzde"
                  >
                    {est.cost > 0 ? "+" : ""}{fmtEur(est.cost)}
                  </span>
                </div>
              </div>
              );
            })}
          </div>

          <button
            onClick={simulate}
            disabled={loading}
            className="btn-primary w-full mt-4 inline-flex items-center justify-center gap-2"
            data-testid="wi-simulate-button"
          >
            <Flask size={14} /> {loading ? "Počítam…" : "Spustiť simuláciu"}
          </button>
        </div>
      </div>

      {/* Right panel: results */}
      <div className="space-y-4">
        {!result && (
          <div className="card-flat p-10 text-center">
            <Flask size={32} weight="duotone" className="mx-auto mb-3 text-[var(--brand)]" />
            <h3 className="font-display text-lg font-semibold">Simulácia</h3>
            <p className="text-sm text-[var(--text-secondary)] mt-2 max-w-md mx-auto">
              Nastavte forecast obdobie, mzdu a zmeny v rozvrhu, potom kliknite „Spustiť simuláciu“.
            </p>
          </div>
        )}

        {result && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="card-flat p-5">
                <p className="label-eyebrow mb-2">Náklady na mzdy</p>
                <p className="font-display text-2xl font-bold tabular">{fmtEur(sum.modified_cost)}</p>
                <p className={`text-xs tabular mt-1 ${sum.cost_delta > 0 ? "text-[var(--critical)]" : sum.cost_delta < 0 ? "text-[var(--success)]" : "text-[var(--text-muted)]"}`}>
                  {sum.cost_delta > 0 ? "▲ +" : sum.cost_delta < 0 ? "▼ " : "± "}{fmtEur(Math.abs(sum.cost_delta))} vs forecast
                </p>
              </div>
              <div className="card-flat p-5">
                <p className="label-eyebrow mb-2">Osobohodiny spolu</p>
                <p className="font-display text-2xl font-bold tabular">{fmtNum(sum.modified_person_hours, 1)}</p>
                <p className={`text-xs tabular mt-1 ${sum.person_hours_delta > 0 ? "text-[var(--critical)]" : sum.person_hours_delta < 0 ? "text-[var(--success)]" : "text-[var(--text-muted)]"}`}>
                  {sum.person_hours_delta > 0 ? "+" : ""}{fmtNum(sum.person_hours_delta, 1)} hod
                </p>
              </div>
              <div className="card-flat p-5">
                <p className="label-eyebrow mb-2">Efektívnosť</p>
                <p className={`font-display text-2xl font-bold tabular ${sum.modified_efficiency_pct >= 100 ? "text-[var(--success)]" : sum.modified_efficiency_pct >= 70 ? "text-[var(--warning)]" : "text-[var(--critical)]"}`}>
                  {fmtPct(sum.modified_efficiency_pct, 1)}
                </p>
                <p className={`text-xs tabular mt-1 ${sum.modified_efficiency_pct > sum.base_efficiency_pct ? "text-[var(--success)]" : sum.modified_efficiency_pct < sum.base_efficiency_pct ? "text-[var(--critical)]" : "text-[var(--text-muted)]"}`}>
                  bázový {fmtPct(sum.base_efficiency_pct, 1)} ({sum.modified_efficiency_pct >= sum.base_efficiency_pct ? "+" : ""}{(sum.modified_efficiency_pct - sum.base_efficiency_pct).toFixed(1)} p.b.)
                </p>
              </div>
            </div>

            <div className="card-flat p-5">
              <h3 className="font-display text-base font-semibold mb-3">Porovnanie: bázový forecast vs s úpravami</h3>
              <DeltaLine label="Osobohodiny" base={sum.base_person_hours} modified={sum.modified_person_hours} fmt={(v) => fmtNum(v, 1)} suffix=" h" />
              <DeltaLine label="Mzdové náklady" base={sum.base_cost} modified={sum.modified_cost} fmt={fmtEur} />
              <DeltaLine label="€ / osobohodina" base={sum.base_per_person_hour} modified={sum.modified_per_person_hour} fmt={fmtEur} />
              <DeltaLine label="Efektívnosť" base={sum.base_efficiency_pct} modified={sum.modified_efficiency_pct} fmt={(v) => fmtNum(v, 1)} suffix=" %" />
              <div className="text-xs text-[var(--text-muted)] mt-3 pt-3 border-t border-[var(--border-default)]">
                Predikovaná tržba sa nemení ({fmtEur(sum.base_pred_revenue)}). Úprava sa týka len personálu — simulácia ukazuje dopad na náklady a efektívnosť pri rovnakej tržbe. Ovplyvnené sloty: <strong>{sum.impacted_slots}</strong>.
              </div>
            </div>

            <div className="card-flat overflow-hidden">
              <div className="px-5 pt-5 pb-3">
                <h3 className="font-display text-sm font-semibold">Detail po dňoch s úpravou</h3>
              </div>
              <div className="overflow-x-auto scroll-area">
                <table>
                  <thead>
                    <tr>
                      <th>Dátum</th>
                      <th>Deň</th>
                      <th className="text-right">Pred. tržba</th>
                      <th className="text-right">Os.hod báz.</th>
                      <th className="text-right">Os.hod upr.</th>
                      <th className="text-right">Δ os.</th>
                      <th className="text-right">Náklady upr.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.days.map((d) => {
                      const cost = d.modified_person_hours * hourlyWage;
                      return (
                        <tr key={d.date}>
                          <td className="tabular">{d.date}</td>
                          <td>{d.weekday_name}</td>
                          <td className="text-right tabular">{fmtEur(d.pred_revenue_total)}</td>
                          <td className="text-right tabular text-[var(--text-secondary)]">{fmtNum(d.pred_person_hours, 1)}</td>
                          <td className="text-right tabular font-medium">{fmtNum(d.modified_person_hours, 1)}</td>
                          <td className={`text-right tabular ${d.day_emp_delta > 0 ? "text-[var(--critical)]" : d.day_emp_delta < 0 ? "text-[var(--success)]" : "text-[var(--text-muted)]"}`}>
                            {d.day_emp_delta > 0 ? "+" : ""}{fmtNum(d.day_emp_delta, 1)}
                          </td>
                          <td className="text-right tabular">{fmtEur(cost)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
      </div>

      <Scenarios
        storeId={storeId}
        period={period}
        forecastFrom={forecastFrom}
        forecastTo={forecastTo}
        hourlyWage={hourlyWage}
        mods={mods}
        onLoad={loadScenario}
      />
    </div>
  );
}
